'use client';
import React, { useState, useRef, useEffect } from 'react';

type Mode = 'idle' | 'image' | 'camera';

export default function QRScannerTool() {
  const [mode, setMode] = useState<Mode>('idle');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopCamera = () => {
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCameraActive(false);
  };

  useEffect(() => { return () => stopCamera(); }, []);

  const scanFromImage = async (file: File) => {
    setError(null);
    setResult(null);
    try {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      const reader = new BrowserQRCodeReader();
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.src = url;
      await new Promise<void>((resolve) => { img.onload = () => resolve(); });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      const imageData = canvas.toDataURL();
      const decoded = await reader.decodeFromImageUrl(imageData);
      setResult(decoded.getText());
      URL.revokeObjectURL(url);
    } catch {
      setError('No QR code found in this image. Please try a clearer image.');
    }
  };

  const startCamera = async () => {
    setError(null);
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);

      const { BrowserQRCodeReader } = await import('@zxing/browser');
      const reader = new BrowserQRCodeReader();

      scanIntervalRef.current = setInterval(async () => {
        if (!videoRef.current) return;
        try {
          const canvas = document.createElement('canvas');
          canvas.width = videoRef.current.videoWidth;
          canvas.height = videoRef.current.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(videoRef.current, 0, 0);
          const decoded = await reader.decodeFromImageUrl(canvas.toDataURL());
          setResult(decoded.getText());
          stopCamera();
        } catch { /* keep scanning */ }
      }, 500);
    } catch {
      setError('Camera access denied. Please allow camera permissions.');
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isUrl = (s: string) => { try { new URL(s); return true; } catch { return false; } };

  return (
    <div className="space-y-5">
      {/* Mode selector */}
      <div className="flex gap-3">
        <button
          onClick={() => { setMode('image'); setResult(null); setError(null); stopCamera(); fileInputRef.current?.click(); }}
          className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 hover:border-brand-blue text-text-secondary hover:text-brand-blue font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>
          Scan from Image
        </button>
        <button
          onClick={() => { setMode('camera'); setResult(null); setError(null); startCamera(); }}
          className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 hover:border-brand-blue text-text-secondary hover:text-brand-blue font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" /><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" /></svg>
          Use Camera
        </button>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) scanFromImage(f); }} />

      {/* Camera view */}
      {cameraActive && (
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-48 h-48 border-2 border-white/60 rounded-2xl" />
          </div>
          <button onClick={stopCamera} className="absolute top-3 right-3 bg-black/50 text-white text-xs px-3 py-1.5 rounded-lg">Stop</button>
          <p className="absolute bottom-3 left-0 right-0 text-center text-white/70 text-xs">Point camera at QR code</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-green-50 border border-green-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            </div>
            <p className="font-semibold text-green-800">QR Code Decoded!</p>
          </div>
          <div className="bg-white rounded-xl p-3 mb-3 border border-green-100">
            <p className="text-text-primary text-sm break-all font-mono">{result}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCopy} className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-colors">
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
            {isUrl(result) && (
              <a href={result} target="_blank" rel="noopener noreferrer" className="flex-1 py-2.5 rounded-xl border border-green-300 text-green-700 font-semibold text-sm text-center hover:bg-green-100 transition-colors">
                Open URL
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
